import { Space } from "antd/es";
import { Guid } from "guid-typescript";
import React, { PureComponent, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { CommonProps } from "../common-props";
import "./horizontal-stack.scss";

interface HorizontalStackProps {
  children?: ReactNode;
  style: React.CSSProperties;
  size?: number;
  mode?: "button-group" | "default";
  align?: "left" | "center" | "right";
  overflowX?: "auto" | "hidden";
}

interface HorizontalStackState {}

class HorizontalStack extends PureComponent<HorizontalStackProps & CommonProps, HorizontalStackState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`hstack_${this.uniqueKey}`);

  private readonly containerProperties = [
    "borderRadius",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomLeftRadius",
    "borderBottomRightRadius",
    "borderWidth",
    "borderLeftWidth",
    "borderRightWidth",
    "borderTopWidth",
    "borderBottomWidth",
    "borderColor",
    "border",
    "borderStyle",
    "background",
    "backgroundColor",
    "backgroundImage",
    "backgroundSize",
    "backgroundPosition",
    "backgroundRepeat",
    "backgroundClip",
    "backgroundOrigin",
    "backgroundAttachment"
  ] as const;

  constructor(props: HorizontalStackProps) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    this.setDynamicStyle();
  }

  componentDidUpdate(prevProps: HorizontalStackProps) {
    if (JSON.stringify(prevProps.style) !== JSON.stringify(this.props.style)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount() {
    this.style.removeStyle();
  }

  private extractContainerProperties = (sourceStyle: React.CSSProperties): React.CSSProperties => {
    const borderStyles: React.CSSProperties = {};

    if (!sourceStyle) {
      return borderStyles;
    }

    this.containerProperties.forEach((property) => {
      const value = sourceStyle[property as keyof React.CSSProperties];
      if (value !== undefined) {
        (borderStyles as any)[property] = value;
      }
    });

    return borderStyles;
  };

  private removeContainerProperties = (style: React.CSSProperties): React.CSSProperties => {
    const cleanedStyle = { ...style };

    this.containerProperties.forEach((property) => {
      if (cleanedStyle[property as keyof React.CSSProperties] !== undefined) {
        delete (cleanedStyle as any)[property];
      }
    });

    return cleanedStyle;
  };

  getProps = () => {
    const props: any = { ...this.props };
    if (props.children) {
      delete props.children;
    }
    if (props.style) {
      delete props.style;
    }
    if (props.size < 0) props.size = 0;
    return props;
  };

  getStyleProp = () => {
    let style: React.CSSProperties = {};

    if (this.props.style) {
      style = this.removeContainerProperties(this.props.style);
    }

    // backgroundColor is on the container div — remove from Space to prevent double layering
    delete style.backgroundColor;

    style.height = "100%";
    style.width = this.props.children ? "max-content" : "100%";

    if (!style.alignItems) {
      style.alignItems = "unset";
    }

    if (this.props.overflowX === "hidden") {
      style.textOverflow = "ellipsis";
      style.whiteSpace = "nowrap";
      style.overflow = "hidden";
      style.maxWidth = "100%";
    }

    return style;
  };

  setDynamicStyle = () => {
    if (!this.uniqueKey) {
      return;
    }

    this.style.setDynamicCss(this.getDynamicCss());
  };

  getUniqueClassName = () => {
    return `k_hstack_${this.uniqueKey.toString().substring(0, 8)}`;
  };

  getDynamicCss = (): string => {
    const className = this.getUniqueClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    const { style } = this.props;

    if (style) {
      if (this.props.size && this.props.size < 0) {
        result += `.${className} .ant-space-item:not(:first-of-type) {
            margin-left: ${this.props.size}px !important;
          }`;
      }

      if (this.props.overflowX === "hidden") {
        result += `.${className} {
          overflow: clip !important;
          overflow-clip-margin: 20px !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .${className} label {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }`;
      }
    }

    return result;
  };

  getClassName = () => {
    let result = this.getUniqueClassName();

    if (this.props.className) {
      result = `${this.props.className} ${result}`;
    }

    return result.trim();
  };

  getJustifyContent = () => {
    if (this.props.align === "left") {
      return "flex-start";
    }

    if (this.props.align === "center") {
      return "center";
    }

    if (this.props.align === "right") {
      return "flex-end";
    }

    return "flex-start";
  };

  getContainerStyle = () => {
    const baseStyle: Record<string, any> = {
      overflowY: "clip",
      overflowClipMargin: "20px"
    };

    if (this.props.overflowX === "hidden") {
      baseStyle.textOverflow = "ellipsis";
      baseStyle.whiteSpace = "nowrap";
      baseStyle.overflow = "clip";
      baseStyle.maxWidth = "100%";
    } else {
      baseStyle.overflowX = this.props.overflowX || "auto";
    }

    const borderStyles = this.extractContainerProperties(this.props.style);
    const containerStyle = { ...baseStyle, ...borderStyles };

    // backgroundColor belongs on the container only (not on inner Space) to prevent
    // double semi-transparent layering that causes visual artifacts
    if (this.props.style?.backgroundColor) {
      containerStyle.backgroundColor = this.props.style.backgroundColor;
    }

    if (this.props.mode === "button-group") {
      return {
        ...containerStyle,
        display: "flex",
        flexDirection: "row",
        justifyContent: this.getJustifyContent()
      };
    }

    return containerStyle;
  };

  render(): ReactNode {
    return (
      <div style={this.getContainerStyle()} className={this.getUniqueClassName()}>
        <Space className={this.getClassName()} style={this.getStyleProp()} {...this.getProps()} direction="horizontal">
          {React.Children.map(this.props.children, (child: any) => {
            if (child?.props?.visibility === "hidden" || child?.props?.children?.props?.visibility === "hidden") {
              return null;
            }
            return <>{child}</>;
          })}
        </Space>
      </div>
    );
  }
}

const horizontalStack = withCommonEvents(HorizontalStack);
export { horizontalStack as HorizontalStack };
